import { apiBaseUrl, auth } from '../firebase';
import { getAuthHeaders } from './vpsApi';

/**
 * Single shared connection to the backend `/events` stream (Server-Sent Events over fetch, because the
 * endpoint needs an Authorization header that EventSource cannot send). Reconnects automatically and
 * refreshes the ID token before it expires.
 */
type Listener = (payload: any) => void;

const listeners = new Set<Listener>();
let started = false;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function dispatch(chunk: string) {
  const data = chunk
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())
    .join('\n');
  if (!data) return;
  try {
    const parsed = JSON.parse(data);
    const payload = parsed && typeof parsed === 'object' && 'payload' in parsed ? parsed.payload : parsed;
    listeners.forEach((fn) => {
      try { fn(payload); } catch { /* listener errors must not break the stream */ }
    });
  } catch {
    // ignore malformed frames
  }
}

async function loop() {
  const base = (apiBaseUrl || '').replace(/\/$/, '');
  let backoff = 1000;
  for (;;) {
    if (!base || !auth.currentUser) {
      await sleep(3000);
      continue;
    }
    const controller = new AbortController();
    const refresh = setTimeout(() => controller.abort(), 45 * 60 * 1000);
    try {
      const headers = await getAuthHeaders();
      headers.set('Accept', 'text/event-stream');
      const res = await fetch(`${base}/events`, { headers, signal: controller.signal, cache: 'no-store' });
      if (!res.ok || !res.body) throw new Error(`sse-${res.status}`);
      backoff = 1000;
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
        let idx = buffer.indexOf('\n\n');
        while (idx >= 0) {
          dispatch(buffer.slice(0, idx));
          buffer = buffer.slice(idx + 2);
          idx = buffer.indexOf('\n\n');
        }
      }
    } catch {
      // network drop / token refresh; reconnect below
    } finally {
      clearTimeout(refresh);
    }
    await sleep(backoff);
    backoff = Math.min(backoff * 2, 30000);
  }
}

export function subscribeRealtime(fn: Listener): () => void {
  listeners.add(fn);
  if (!started) {
    started = true;
    void loop();
  }
  return () => { listeners.delete(fn); };
}
